
import { Component, ErrorInfo, ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

/**
 * Error Boundary component to catch JavaScript errors in child components
 * and display a fallback UI instead of crashing the whole application
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Enhanced error logging with security considerations
    const sanitizedError = {
      message: error.message || 'Unknown error',
      stack: process.env.NODE_ENV === 'development' ? error.stack : undefined,
      component: errorInfo.componentStack?.split('\n')[0] || 'Unknown component',
      timestamp: new Date().toISOString(),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'Unknown',
      url: typeof window !== 'undefined' ? window.location.href : 'Unknown'
    };
    
    console.error('ErrorBoundary caught an error:', sanitizedError);
    
    // In production, send to error monitoring service
    if (process.env.NODE_ENV === 'production') {
      // This would send to your error monitoring service
      // Example: Sentry, LogRocket, etc.
    }
    
    // Update state with error info (only in development)
    this.setState({ 
      errorInfo: process.env.NODE_ENV === 'development' ? errorInfo : undefined 
    });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return this.props.fallback || (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
          <div className="text-center max-w-md">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Something went wrong</h2>
            <p className="text-gray-600 mb-6">
              We're sorry, but an error occurred while rendering this page.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Error: {this.state.error?.toString() || 'Unknown error'}
            </p>
            {this.state.error?.message && (
              <p className="text-sm text-gray-500 mb-6">
                Message: {this.state.error.message}
              </p>
            )}
            <details className="mb-6 text-left">
              <summary className="text-sm text-gray-500 cursor-pointer">
                Error details
              </summary>
              <pre className="mt-2 whitespace-pre-wrap text-xs bg-gray-100 p-4 rounded overflow-auto max-h-[200px]">
                {this.state.error?.stack || 'No stack trace available'}
              </pre>
            </details>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-edgefi-purple text-white rounded-lg hover:bg-edgefi-purple/90 transition-colors"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
